import React from 'react';
import { Trash2 } from 'lucide-react';

/** Phase-1-Dummy-Texte für den Banker-Pitch im PE-Profit-Report */
const DUMMY_AUSGANGSLAGE =
  'Der Salon verzeichnet stabile Umsätze bei steigendem Wettbewerbsdruck. Die bestehende Ausstattung und die aktuelle Kapazität entsprechen nicht mehr dem Qualitätsanspruch der Zielkundschaft. Eine gezielte Investition soll die Marktposition sichern und die Auslastung in Peak-Zeiten erhöhen.';

const DUMMY_MASSNAHMEN =
  'Geplant sind die Modernisierung zentraler Arbeitsplätze, die Optimierung der Empfangszone sowie die Einführung eines digital gestützten Termin- und Kapazitätsmanagements. Parallel werden Preisstruktur und Leistungsportfolio an die neue Positionierung angepasst.';

const DUMMY_ROI =
  'Bei konservativer Annahme einer Umsatzsteigerung von 8–12 % innerhalb von 18 Monaten amortisiert sich die Investition im prognostizierten Zeitraum. Die Maßnahme stärkt die Ertragskraft und verbessert die bankrelevante Nachvollziehbarkeit der Planung.';

export type BankerPitchSections = {
  ausgangslage: string;
  massnahmen: string;
  roi: string;
};

/** Persistierbarer Dummy-Pitch (JSON-String) für PlanungsSzenario.bankerPitchText */
export const buildDummyBankerPitchText = (): string =>
  JSON.stringify({
    ausgangslage: DUMMY_AUSGANGSLAGE,
    massnahmen: DUMMY_MASSNAHMEN,
    roi: DUMMY_ROI,
  } satisfies BankerPitchSections);

export const parseBankerPitchText = (text: string): BankerPitchSections => {
  try {
    const parsed = JSON.parse(text) as Partial<BankerPitchSections>;
    if (
      parsed &&
      typeof parsed.ausgangslage === 'string' &&
      typeof parsed.massnahmen === 'string' &&
      typeof parsed.roi === 'string'
    ) {
      return {
        ausgangslage: parsed.ausgangslage,
        massnahmen: parsed.massnahmen,
        roi: parsed.roi,
      };
    }
  } catch {
    /* plain text fallback */
  }
  return {
    ausgangslage: text,
    massnahmen: '',
    roi: '',
  };
};

interface BankerPitchReportSectionProps {
  text: string;
  onClear?: () => void;
}

const BankerPitchReportSection: React.FC<BankerPitchReportSectionProps> = ({ text, onClear }) => {
  const sections = parseBankerPitchText(text);
  const erstelltAm = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <section className="pe-profit-banker-pitch mt-10 rounded-xl border-2 border-[#E6C299]/60 bg-[#faf8f5] p-6 md:p-8">
      {/* Einzelne pdf-sections: PDF-Export kann Restplatz füllen statt einen Riesenblock zu verschieben */}
      <header className="pe-profit-pdf-section pe-profit-banker-pitch-block mb-6 border-b border-[#E6C299]/50 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">
              PreisExpert · Bankschreiben
            </p>
            <h3 className="mt-1 text-xl font-bold text-slate-900 md:text-2xl">
              <span className="italic text-[#E6C299]">PE</span>
              <span className="font-bold text-slate-900">Profit</span> — KI-Text für die Bank
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Entwurf · Erstellt am {erstelltAm}
            </p>
          </div>
          {onClear ? (
            <button
              type="button"
              onClick={onClear}
              className="pe-profit-no-print inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
              title="KI-Text löschen"
              aria-label="KI-Text löschen"
            >
              <Trash2 size={14} />
              Löschen
            </button>
          ) : null}
        </div>
      </header>

      <p className="pe-profit-pdf-section mb-6 text-sm leading-relaxed text-slate-600">
        Strukturiertes Schreiben zur bankkonformen Darstellung von Ausgangslage, geplanten Maßnahmen
        und erwarteter wirtschaftlicher Wirkung (ROI).
      </p>

      <div className="pe-profit-pdf-section mb-6">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#b48a56]">
          1. Ausgangslage
        </h4>
        <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-line">
          {sections.ausgangslage}
        </p>
      </div>

      {sections.massnahmen ? (
        <div className="pe-profit-pdf-section mb-6">
          <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#b48a56]">
            2. Maßnahmen
          </h4>
          <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-line">
            {sections.massnahmen}
          </p>
        </div>
      ) : null}

      {sections.roi ? (
        <div className="pe-profit-pdf-section">
          <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#b48a56]">
            3. ROI
          </h4>
          <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-line">
            {sections.roi}
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default BankerPitchReportSection;
